import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack,
  Container,
  Fade,
  Zoom,
  Avatar,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Security,
  Email,
  Timer,
  Info,
  Person,
  Badge,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Palette as PaletteIcon,
  Token as TokenIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import axios from 'axios';
import LinearProgress from '@mui/material/LinearProgress';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { mode, setMode } = useThemeMode();
  const isDark = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorSuccess, setTwoFactorSuccess] = useState('');

  // Token usage state
  const [usageStats, setUsageStats] = useState<{
    token_limit: number | null;
    tokens_used_this_month: number;
    tokens_remaining: number | null;
    usage_percent: number;
    quota_enabled: boolean;
    reset_at: string;
  } | null>(null);

  // Load user profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const [profileRes, usageRes] = await Promise.all([
          axios.get('/api/auth/profile', { headers: { Authorization: `Bearer ${token}` } }),
          axios.get('/api/auth/usage', { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setTwoFactorEnabled(profileRes.data.user.two_factor_enabled || false);
        setUsageStats(usageRes.data);
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };

    loadProfile();
  }, []);

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    setPasswordLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/auth/change-password',
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPasswordSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle 2FA toggle
  const handleTwoFactorToggle = async () => {
    setTwoFactorError('');
    setTwoFactorSuccess('');
    setTwoFactorLoading(true);

    try {
      const token = localStorage.getItem('token');
      const endpoint = twoFactorEnabled ? '/api/auth/disable-2fa' : '/api/auth/enable-2fa';

      await axios.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTwoFactorEnabled(!twoFactorEnabled);
      setTwoFactorSuccess(
        twoFactorEnabled
          ? '2FA disabled. You will only need your password to login.'
          : '2FA enabled! Next time you login, you will receive a 6-digit code via email.'
      );
    } catch (error: any) {
      setTwoFactorError(error.response?.data?.error || 'Failed to update 2FA settings');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // Handle theme mode change
  const handleThemeModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: string | null) => {
    if (newMode !== null) {
      setMode(newMode as 'light' | 'dark');
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Enhanced Header with Gradient Background */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 4,
              mb: 4,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Security sx={{ fontSize: 36 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" gutterBottom>
                    Security Settings
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Manage your account security, authentication preferences, and appearance
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>

        <Grid container spacing={3}>
          {/* Appearance / Theme Section */}
          <Grid item xs={12} lg={8}>
            <Fade in timeout={700}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'primary.light',
                      color: 'primary.main',
                      width: 48,
                      height: 48
                    }}
                  >
                    <PaletteIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Appearance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Customize how GuardLLM looks on your device
                    </Typography>
                  </Box>
                </Box>

                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'divider',
                    borderWidth: 2,
                    mb: 3
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                      Theme Mode
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Choose between dark and light mode. Your preference is saved automatically.
                    </Typography>

                    <ToggleButtonGroup
                      value={mode}
                      exclusive
                      onChange={handleThemeModeChange}
                      aria-label="theme mode"
                      sx={{
                        width: '100%',
                        '& .MuiToggleButton-root': {
                          flex: 1,
                          py: 2.5,
                          px: 3,
                          borderRadius: '12px !important',
                          border: `2px solid ${isDark ? '#334155' : '#e2e8f0'} !important`,
                          mx: 1,
                          textTransform: 'none',
                          transition: 'all 0.3s ease',
                          '&.Mui-selected': {
                            background: isDark
                              ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(236, 72, 153, 0.1) 100%)'
                              : 'linear-gradient(135deg, #eef2ff 0%, #fce7f3 100%)',
                            borderColor: '#6366f1 !important',
                            color: isDark ? '#a5b4fc' : '#6366f1',
                            '&:hover': {
                              background: isDark
                                ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(236, 72, 153, 0.15) 100%)'
                                : 'linear-gradient(135deg, #e0e7ff 0%, #fce7f3 100%)'
                            }
                          }
                        }
                      }}
                    >
                      <ToggleButton value="dark" aria-label="dark mode">
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 56,
                              height: 56,
                              bgcolor:
                                mode === 'dark'
                                  ? 'rgba(99, 102, 241, 0.2)'
                                  : isDark
                                    ? '#334155'
                                    : '#f1f5f9',
                              color: mode === 'dark' ? '#818cf8' : isDark ? '#94a3b8' : '#64748b',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <DarkModeIcon sx={{ fontSize: 28 }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              sx={{
                                color:
                                  mode === 'dark'
                                    ? isDark
                                      ? '#a5b4fc'
                                      : '#6366f1'
                                    : 'text.primary'
                              }}
                            >
                              Dark Mode
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', display: 'block' }}
                            >
                              Comfortable on the eyes
                            </Typography>
                          </Box>
                          {mode === 'dark' && (
                            <Chip
                              label="Active"
                              size="small"
                              color="primary"
                              icon={<CheckCircleIcon />}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </ToggleButton>

                      <ToggleButton value="light" aria-label="light mode">
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 56,
                              height: 56,
                              bgcolor:
                                mode === 'light'
                                  ? 'rgba(245, 158, 11, 0.15)'
                                  : isDark
                                    ? '#334155'
                                    : '#f1f5f9',
                              color: mode === 'light' ? '#f59e0b' : isDark ? '#94a3b8' : '#64748b',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <LightModeIcon sx={{ fontSize: 28 }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              sx={{
                                color:
                                  mode === 'light'
                                    ? isDark
                                      ? '#fbbf24'
                                      : '#d97706'
                                    : 'text.primary'
                              }}
                            >
                              Light Mode
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', display: 'block' }}
                            >
                              Clean and bright
                            </Typography>
                          </Box>
                          {mode === 'light' && (
                            <Chip
                              label="Active"
                              size="small"
                              color="warning"
                              icon={<CheckCircleIcon />}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </CardContent>
                </Card>

                {/* Preview card */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    bgcolor: 'background.default',
                    borderRadius: 2
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Info color="primary" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight="bold">
                      About Theme Preferences
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Your theme preference is saved locally on this device and will persist across
                    sessions. The default theme is <strong>Dark Mode</strong> for a comfortable
                    viewing experience.
                  </Typography>
                </Paper>
              </Paper>
            </Fade>
          </Grid>

          {/* Account Information Sidebar */}
          <Grid item xs={12} lg={4}>
            <Fade in timeout={1000}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <Avatar
                    sx={{
                      bgcolor: isDark ? 'rgba(59, 130, 246, 0.15)' : 'info.light',
                      color: 'info.main'
                    }}
                  >
                    <Person />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    Account Information
                  </Typography>
                </Box>
                <Stack spacing={3}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Email Address
                      </Typography>
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {user?.email || 'Not available'}
                      </Typography>
                    </Paper>
                  </Box>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Badge fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Account Role
                      </Typography>
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Chip
                        label={
                          user?.role
                            ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                            : 'User'
                        }
                        color="primary"
                        size="small"
                      />
                    </Paper>
                  </Box>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PaletteIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Current Theme
                      </Typography>
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Chip
                        label={mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
                        icon={mode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
                        color={mode === 'dark' ? 'primary' : 'warning'}
                        size="small"
                      />
                    </Paper>
                  </Box>

                  {/* Token Usage */}
                  {usageStats && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <TokenIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                          Token Usage This Month
                        </Typography>
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}
                      >
                        {usageStats.quota_enabled ? (
                          <>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2" fontWeight="medium">
                                {(usageStats.tokens_used_this_month ?? 0).toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                / {usageStats.token_limit!.toLocaleString()}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={usageStats.usage_percent}
                              color={
                                usageStats.usage_percent >= 90
                                  ? 'error'
                                  : usageStats.usage_percent >= 70
                                    ? 'warning'
                                    : 'primary'
                              }
                              sx={{ borderRadius: 1, height: 6, mb: 1 }}
                            />
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                {usageStats.usage_percent}% used
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {usageStats.tokens_remaining!.toLocaleString()} left
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="medium">
                              {(usageStats.tokens_used_this_month ?? 0).toLocaleString()} used
                            </Typography>
                            <Chip
                              label="Unlimited"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mt={0.5}
                        >
                          Resets{' '}
                          {new Date(usageStats.reset_at).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Fade>
          </Grid>

          {/* Two-Factor Authentication Section */}
          <Grid item xs={12} lg={8}>
            <Fade in timeout={800}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'primary.light',
                      color: 'primary.main',
                      width: 48,
                      height: 48
                    }}
                  >
                    <ShieldIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Two-Factor Authentication (2FA)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add an extra layer of security to your account
                    </Typography>
                  </Box>
                </Box>

                <Card
                  variant="outlined"
                  sx={{
                    bgcolor: twoFactorEnabled
                      ? isDark
                        ? 'rgba(16, 185, 129, 0.1)'
                        : 'success.light'
                      : isDark
                        ? 'rgba(255, 255, 255, 0.03)'
                        : 'grey.50',
                    borderColor: twoFactorEnabled ? 'success.main' : 'divider',
                    borderWidth: 2,
                    borderRadius: 2,
                    mb: 3
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            {twoFactorEnabled ? 'Currently Enabled' : 'Currently Disabled'}
                          </Typography>
                          <Chip
                            label={twoFactorEnabled ? 'Active' : 'Inactive'}
                            color={twoFactorEnabled ? 'success' : 'default'}
                            size="small"
                            icon={twoFactorEnabled ? <CheckCircleIcon /> : <CancelIcon />}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {twoFactorEnabled
                            ? 'Your account is protected with 2FA. You will need a code from your email to login.'
                            : 'Enable 2FA to add an extra security layer to your account.'}
                        </Typography>
                      </Box>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={twoFactorEnabled}
                            onChange={handleTwoFactorToggle}
                            disabled={twoFactorLoading}
                            color="primary"
                            size="medium"
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight="bold">
                            {twoFactorEnabled ? 'Disable' : 'Enable'}
                          </Typography>
                        }
                        labelPlacement="start"
                      />
                    </Stack>

                    {twoFactorError && (
                      <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                        {twoFactorError}
                      </Alert>
                    )}

                    {twoFactorSuccess && (
                      <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                        {twoFactorSuccess}
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                {/* How it works */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    bgcolor: 'background.default',
                    borderRadius: 2
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Info color="primary" />
                    <Typography variant="subtitle2" fontWeight="bold">
                      How 2FA Works:
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'primary.light',
                            color: 'primary.main'
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            1
                          </Typography>
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary="Enter your email and password as usual"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'primary.light',
                            color: 'primary.main'
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            2
                          </Typography>
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary="Receive a 6-digit code via email"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'primary.light',
                            color: 'primary.main'
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            3
                          </Typography>
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary="Enter the code to complete login"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Timer fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Code expires in 10 minutes
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        2FA is mandatory for all new users (recommended for existing users)
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Paper>
            </Fade>
          </Grid>

          {/* Change Password Section */}
          <Grid item xs={12}>
            <Zoom in timeout={1200}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: isDark ? 'rgba(245, 158, 11, 0.15)' : 'warning.light',
                      color: 'warning.main',
                      width: 48,
                      height: 48
                    }}
                  >
                    <LockIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Change Password
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Update your password to keep your account secure
                    </Typography>
                  </Box>
                </Box>

                {passwordError && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {passwordError}
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                    {passwordSuccess}
                  </Alert>
                )}

                <Box component="form" onSubmit={handlePasswordChange}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="password"
                        label="Current Password"
                        variant="outlined"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        placeholder="Enter your current password"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="password"
                        label="New Password"
                        variant="outlined"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        helperText="Minimum 8 characters"
                        placeholder="Enter new password"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="password"
                        label="Confirm New Password"
                        variant="outlined"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        placeholder="Confirm new password"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={passwordLoading}
                        startIcon={<LockIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          px: 4,
                          py: 1.5,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)'
                          }
                        }}
                      >
                        {passwordLoading ? 'Updating Password...' : 'Update Password'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Profile;
